import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Grid from '../components/Grid'; 
import { FaEye } from "react-icons/fa"; 
import { DataGrid } from '@mui/x-data-grid';
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Settlement({ onAddSettlementClick }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [settlements, setSettlements] = useState([]); // Note: Corrected variable name from 'settlemets' to 'settlements'
  const [sort] = useState({
    field: 'date',
    direction: 'asc',
  });
  const [selectedSearchField, setSelectedSearchField] = useState('settlement_id');
  const [selectedSettlement, setSelectedSettlement] = useState(null);


  useEffect(() => {
    const fetchSettlementsReceipts = async () => {
      try {
        const token = sessionStorage.getItem('token');
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  
        const response = await axios.get(`${API_BASE_URL}/settlements/getSettlements`, {
          headers: { Authorization: `Bearer ${token}` },
        });

  
        if (response.data) {
          const formattedSettlements = response.data.map(settlement => ({
            id: settlement.settlement_id,
            settlement_id: settlement.settlement_id,
            settledAmount: settlement.settledAmount,
            settlementDate: settlement.settlementDate,
            createdAt: settlement.createdAt,
            invoice_id: settlement.invoiceId?.invoice_id || 'N/A',
            customer_id: settlement.invoiceId?.customer_id?.customer_code || 'N/A',
            customer_name: settlement.invoiceId?.customer_id?.customer_name || 'N/A',
            invoice_balance: settlement.invoiceId?.blance || 0, // ✅ Added here
            createdBy: settlement.createdBy?.username || "Unknown",
          }));
  
          setSettlements(formattedSettlements);

        } else {
          toast.error("No settlements data received.");
        }
      } catch (error) {
        console.error("Error fetching settlements:", error);
        toast.error("Error fetching settlements");
      }
    };
  
    fetchSettlementsReceipts();
  }, []);

   const handleDownloadExcel = async () => {
    const dataToExport = searchTerm.trim().length > 0 ? filteredSettlements : settlements;

    if (dataToExport.length === 0) {
      toast.warn("No data available to download.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Settlements");

    // Add header row with styling
    const headerRow = [
      "Settlement ID",
      "Settlement Date",
      "Invoice ID",
      "Customer ID",
      "Customer Name",
      "Settled Amount (LKR)",
      "Invoice Balance (LKR)",
      "Created By"
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add data rows
    dataToExport.forEach((item) => {
      worksheet.addRow([
        item.settlement_id,
        item.settlementDate,
        item.invoice_id,
        item.customer_id,
        item.customer_name,
        Number(item.settledAmount || 0).toFixed(2),
        Number(item.invoice_balance || 0).toFixed(2),
        item.createdBy
      ]);
    });

    // Adjust column widths
    worksheet.columns.forEach((column) => {
      column.width = column.header
        ? column.header.toString().length < 20
          ? 20
          : column.header.toString().length
        : 20;
    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "settlements.xlsx");
  };

  const fetchSettlementDetails = async (settlement_id) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/settlements/getSettlementById/${settlement_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedSettlement(response.data);
    } catch (error) {
      console.error("Error fetching settlement details:", error);
      toast.error("Error fetching settlement details");
    }
  };

  const filteredSettlements = settlements
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Sort by latest created first
    .filter((settlement) => {
      const fieldValue = settlement[selectedSearchField];

      if (fieldValue === null || fieldValue === undefined) return false;

      // Convert to string, then to lowercase for comparison
      return fieldValue.toString().toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      if (a[sort.field] < b[sort.field]) return sort.direction === 'asc' ? -1 : 1;
      if (a[sort.field] > b[sort.field]) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });

  const columns = [
    { field: 'settlement_id', headerName: 'Settlement ID', flex: 1 },
    { field: 'settlementDate', headerName: 'Settlement Date', flex: 1,
      renderCell: (params) => (
        <span>
          {params.value ? new Date(params.value).toLocaleDateString("en-GB") : "N/A"}
        </span>
      ),
    },
    { field: 'invoice_id', headerName: 'Invoice ID', flex: 1 },
    { field: 'customer_id', headerName: 'Customer ID', flex: 1 },
    { field: 'customer_name', headerName: 'Customer Name', flex: 1 },
    { field: 'settledAmount', headerName: 'Settled Amount (LKR)', flex: 1, headerAlign: "right", align: "right", renderCell: (params) => `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` },
    {
      field: 'invoice_balance',
      headerName: 'Invoice Balance (LKR)',
      flex: 1,
      headerAlign: "right",
      align: "right",
      renderCell: (params) =>
        `${new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value || 0)}`
    },
    {
        field: 'createdBy',
        headerName: "Created By",
        flex: 1,
      },
    {
      field: 'action', 
      headerName: 'Action', 
      flex: 1, 
      headerAlign: "center",
      renderCell: (params) => (
        <div className="flex justify-center items-center p-3 space-x-3">
          <button
            onClick={() => fetchSettlementDetails(params.id)}
            className="bg-[#FBC02D] text-white p-2 flex items-center justify-center"
          >
            <FaEye className="text-white" />
          </button>
        </div>
      ),
    },
  ];
  

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Settlements</h1>
          <button onClick={onAddSettlementClick} className="bg-green-700 text-white px-4 py-2 rounded-lg">
            Add Settlement
          </button>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="settlement_id">Settlement ID</option>
                <option value="settledAmount">Amount</option>
                <option value="settlementDate">Date</option>
                <option value="invoice_id">Invoice ID</option>
                <option value="customer_id">Customer ID</option>
                <option value="customer_name">Customer Name</option>
                <option value="createdBy">Created By</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search payment receipts"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>
          <Grid rows={filteredSettlements} columns={columns} getRowId={(row) => row.settlement_id}/>
          {selectedSettlement && (
            <div
              className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
              style={{ overflowY: "auto" }}
            >
              <div
                className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto"
                style={{ maxHeight: "80vh", overflowY: "auto",  }}
              >
                <button
                  onClick={() => setSelectedSettlement(null)}
                  className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-lg hover:bg-red-700"
                >
                  &times;
                </button>
                <h2 className="text-3xl mb-4 block text-black-700 font-bold">Settlement Details</h2>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Settlement ID</label>
                  <input
                    type="text"
                    value={selectedSettlement.settlement_id || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Settled Amount</label>
                  <input
                    type="text"
                    value={selectedSettlement.settledAmount || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Settlement Date</label>
                  <input
                    type="text"
                    value={
                      selectedSettlement.settlementDate
                        ? selectedSettlement.settlementDate.slice(0, 10)
                        : ""
                    }
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Invoice ID</label>
                  <input
                    type="text"
                    value={selectedSettlement.invoiceId.invoice_id || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Invoice Amount</label>
                  <input
                    type="text"
                    value={selectedSettlement.invoiceId.amount || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Invoice Balance</label>
                  <input
                    type="text"
                    value={selectedSettlement.invoiceId.blance || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-black-700 font-semibold">Customer Name</label>
                  <input
                    type="text"
                    value={selectedSettlement.invoiceId.customer_id.customer_name || ""}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                {/* Display payment receipts & credit notes*/}
                <div className='grid grid-cols-2 gap-4'>
                  <h3 className="text-lg font-semibold mt-4 mb-2">Payment Receipts</h3>
                  <h3 className="text-lg font-semibold mt-4 mb-2">Credit Notes</h3>
                </div>
                <div className='mb-4 grid grid-cols-2 gap-4'>
                  <div  style={{ height: 400, width: "100%" }}>
                    <DataGrid
                    rows={selectedSettlement.payment_receipts.map((receipt, index) => ({
                      id: index + 1,
                      receiptId: receipt.payment_receipt_id.payment_receipt_id,
                      amount_applied: receipt.amount_applied,
                    }))}
                    columns={[
                      { field: "id", headerName: "ID", width: 80, flex: 0.3 },
                      { field: "receiptId", headerName: "Payment Receipt ID", width: 150, flex: 1 },
                      { 
                        field: "amount_applied", 
                        headerName: "Applied Amount (LKR)", 
                        width: 140, 
                        flex: 1, 
                        headerAlign: "right",
                        align: "right",
                        renderCell: (params) => 
                        `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` 
                      },
                    ]}
                    pageSize={16}
                    />
                  </div>
                  <div  style={{ height: 400, width: "100%" }}>
                    <DataGrid
                    rows={selectedSettlement.credit_notes.map((note, index) => ({
                      id: index + 1,
                      noteId: note.credit_note_id.credit_note_code,
                      amount_applied: note.credited_amount,
                    }))}
                    columns={[
                      { field: "id", headerName: "ID", width: 80, flex: 0.3 },
                      { field: "noteId", headerName: "Credit Note ID", width: 150, flex: 1 },
                      { 
                        field: "amount_applied", 
                        headerName: "Credited Amount (LKR)", 
                        width: 140, 
                        flex: 1, 
                        headerAlign: "right",
                        align: "right",
                        renderCell: (params) => 
                        `${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` 
                      },
                    ]}
                    pageSize={16}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settlement;
